import {
  FlexibleConnectedPositionStrategyOrigin,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ComponentPortal } from '@angular/cdk/portal';
import { SearchOptionsOverlayComponent } from '../components/search/search-options/search-options-overlay/search-options-overlay.component';
import { keyStrokes, OverlayPositions } from '../utility/constants';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root',
})
export class SearchOverlayService {
  private searchOverlayRef?: OverlayRef;
  private hyperlinkSub?: Subscription;
  private outsideClickSub?: Subscription;
  private escapeSub?: Subscription;

  constructor(
    private readonly overlayPositionBuilder: OverlayPositionBuilder,
    private readonly overlay: Overlay,
    private readonly dialog: MatDialog
  ) {}

  public get isOverlayOpen(): boolean {
    return !!this.searchOverlayRef?.hasAttached();
  }

  /** `onEscape` runs after Esc closes the menu, e.g. to move focus back to the page. */
  public openSearchOverlay(
    searchOverlay: FlexibleConnectedPositionStrategyOrigin,
    isFromModule: string,
    focusFirstItem = false,
    onEscape?: () => void
  ) {
    this.closeOverlay();
    this.getOverlayConfig(searchOverlay);
    const tooltipPortal = new ComponentPortal(SearchOptionsOverlayComponent);
    const tooltipRef = this.searchOverlayRef!.attach(tooltipPortal);
    tooltipRef.instance.isFrom = isFromModule;
    tooltipRef.instance.focusFirstItem = focusFirstItem;
    this.hyperlinkSub = tooltipRef.instance.hyperlinkClicked
      .pipe(take(1))
      .subscribe(() => {
        this.closeOverlay();
      });
    this.listenForOutsideClick();
    this.listenForEscape(onEscape);
  }

  public closeOverlay() {
    this.hyperlinkSub?.unsubscribe();
    this.hyperlinkSub = undefined;
    this.outsideClickSub?.unsubscribe();
    this.outsideClickSub = undefined;
    this.escapeSub?.unsubscribe();
    this.escapeSub = undefined;
    if (this.searchOverlayRef) {
      this.searchOverlayRef.dispose();
      this.searchOverlayRef = undefined;
    }
  }

  // Close on a click outside the overlay. Clicks inside any CDK overlay (this
  // panel, dialogs opened from it, tooltips) are ignored. The timestamp check
  // skips the click that opened the overlay, which is still bubbling up to the
  // document when this listener is added.
  private listenForOutsideClick() {
    const openedAt = performance.now();
    this.outsideClickSub = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => event.timeStamp > openedAt),
        filter(
          event =>
            !(event.target as HTMLElement | null)?.closest(
              '.cdk-overlay-container'
            )
        )
      )
      .subscribe(() => this.closeOverlay());
  }

  // Esc closes the menu on every page that opens it (summary and advanced search). Window
  // capture runs before `appFocusOnShortcut`'s document capture handler, which stops
  // propagation. An open dialog owns Esc, so it is left alone.
  private listenForEscape(onEscape?: () => void) {
    this.escapeSub = fromEvent<KeyboardEvent>(window, 'keydown', {
      capture: true,
    })
      .pipe(
        filter(
          event =>
            event.key === keyStrokes.escape && !this.dialog.openDialogs.length
        )
      )
      .subscribe(() => {
        this.closeOverlay();
        onEscape?.();
      });
  }

  private getOverlayConfig(
    searchOverlay: FlexibleConnectedPositionStrategyOrigin
  ) {
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(searchOverlay)
      .withPositions([
        {
          originX: OverlayPositions.end,
          originY: OverlayPositions.top,
          overlayX: OverlayPositions.end,
          overlayY: OverlayPositions.top,
          offsetX: 0,
          offsetY: 10,
        },
      ]);
    this.searchOverlayRef = this.overlay.create({
      positionStrategy,
      disposeOnNavigation: true,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
  }
}
